
























































































import Vue from "vue";
import ApiResponse from "@/models/ApiResponse";
import IconButton from "@/components/IconButton.vue";
import DocumentType from "@/modules/document/models/DocumentTypeModel";
import http, { checkResponse, handleApiResponse } from "@/services/http";
import eventBus from "@/services/eventBus";

interface GenoInvoiceDocument {
  id: number;
  fileName: string;
  fileExtension: string;
  documentType?: string;
  typeName?: string;
}

interface FileInputRef extends Vue {
  $refs: {
    input: HTMLInputElement;
  };
}

interface Data {
  documents: GenoInvoiceDocument[];
  documentTypes: DocumentType[];
  isCreating: boolean;
  isLoading: boolean;
  newDocumentTypeId: number;
}

interface Props {
  genoId: number;
  disabled: boolean;
  table: string;
  column: string;
}

interface Methods {
  loadDocuments(): Promise<void>;
  loadDocumentTypes(): Promise<void>;
  uploadDocument(): Promise<void>;
  deleteDocument(document: GenoInvoiceDocument): Promise<void>;
  downloadFile(document: GenoInvoiceDocument): void;
  cancelCreate(): void;
  emitDocuments(): void;
  getFileName(document: GenoInvoiceDocument): string;
}

export default Vue.extend<Data, Methods, {}, Props>({
  components: {
    IconButton,
  },

  props: {
    genoId: { type: Number, default: 1 },
    disabled: { type: Boolean, default: false },
    table: { type: String, default: "hmouigeninvoice" },
    column: { type: String, default: "szam" },
  },

  data() {
    return {
      documents: [],
      documentTypes: [],
      isCreating: false,
      isLoading: false,
      newDocumentTypeId: 1,
    };
  },

  async beforeMount() {
    await Promise.all([this.loadDocumentTypes(), this.loadDocuments()]);
  },

  methods: {
    async loadDocuments() {
      if (this.$props.genoId < 2) {
        this.documents = [];
        this.emitDocuments();
        return;
      }

      this.isLoading = true;
      try {
        this.documents = await http
          .post<ApiResponse<GenoInvoiceDocument[]>>("documents/all", {
            statementId: this.$props.genoId,
            table: this.$props.table,
            column: this.$props.column,
          })
          .then(handleApiResponse);
        this.emitDocuments();
      } catch (ex) {
        eventBus.$emit("error", "Nem sikerült a dokumentumok betöltése!");
      } finally {
        this.isLoading = false;
      }
    },

    async loadDocumentTypes() {
      try {
        this.documentTypes = await http
          .post<ApiResponse<DocumentType[]>>("documents/alltypes", {
            table: this.$props.table,
            column: this.$props.column,
          })
          .then(handleApiResponse);
      } catch (ex) {
        this.documentTypes = [];
      }

      if (this.documentTypes.length === 0) {
        this.documentTypes = await http
          .get<ApiResponse<DocumentType[]>>("document-types")
          .then(handleApiResponse)
          .then((types) => types.filter((type) => !type.deleted && !type.readOnly));
      }

      this.newDocumentTypeId = this.documentTypes[0]?.id ?? 1;
    },

    async uploadDocument() {
      const input = (this.$refs.newFile as FileInputRef | undefined)?.$refs?.input;
      const file = input?.files?.[0];
      if (!file || this.$props.genoId < 2) {
        return;
      }

      this.isLoading = true;
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("typeID", this.newDocumentTypeId.toString());
        data.append("relatedItemName", `${this.$props.table}.${this.$props.column}`);
        data.append("relatedItemID", this.$props.genoId.toString());
        data.append("description", "");

        await http
          .post<ApiResponse<GenoInvoiceDocument>>("documents", data, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then(handleApiResponse);

        eventBus.$emit("success", "Sikeres mentés!");
        this.cancelCreate();
        await this.loadDocuments();
      } catch (ex) {
        eventBus.$emit("error", "Nem sikerült a dokumentum feltöltése!");
      } finally {
        this.isLoading = false;
      }
    },

    async deleteDocument(document: GenoInvoiceDocument) {
      if (!document.id) {
        return;
      }

      this.isLoading = true;
      try {
        await http.delete(`documents/delete/${document.id}`).then(checkResponse);
        this.documents = this.documents.filter((item) => item.id !== document.id);
        this.emitDocuments();
        eventBus.$emit("success", "Sikeres törlés!");
      } catch (ex) {
        eventBus.$emit("error", "Nem sikerült a dokumentum törlése!");
      } finally {
        this.isLoading = false;
      }
    },

    downloadFile(document: GenoInvoiceDocument) {
      window.open(
        `${http.defaults.baseURL}document/download/${document.id}?connectionKey=${http.defaults.headers["X-TruFinance-ConnectionID"]}`
      );
    },

    cancelCreate() {
      this.isCreating = false;
      this.newDocumentTypeId = this.documentTypes[0]?.id ?? 1;
    },

    emitDocuments() {
      this.$emit("update:documents", this.documents.map((document) => document.id));
    },

    getFileName(document: GenoInvoiceDocument) {
      const extension = document.fileExtension ?? "";
      const fileName = document.fileName ?? "";
      return fileName.endsWith(extension) ? fileName : `${fileName}${extension}`;
    },
  },

  watch: {
    genoId() {
      this.loadDocuments();
    },
  },
});
